import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { OnboardingErrorBoundary } from '@/components/onboarding/OnboardingErrorBoundary';
import { OnboardingWizard } from '@/components/onboarding/OnboardingWizard';
import { CenteredSpinner } from '@/components/ui/centered-spinner';
import { useGitBaseUrl } from '@/hooks/use-org';
import { sessionQueryOptions } from '@/lib/auth-query-options';
import { listOrgsQueryOptions } from '@/lib/org-query-options';

export const Route = createFileRoute('/onboarding')({
  ssr: 'data-only',
  beforeLoad: async ({ context: { queryClient }, location }) => {
    const session = await queryClient.ensureQueryData(sessionQueryOptions);
    if (!session) {
      throw redirect({ to: '/sign-in' });
    }

    if (location.search && 'debug' in (location.search as Record<string, unknown>)) {
      return {};
    }
    return {};
  },
  component: OnboardingPage,
});

function OnboardingPage() {
  const navigate = useNavigate();
  const gitBaseUrl = useGitBaseUrl();
  const { data: orgs, isPending } = useQuery(listOrgsQueryOptions);
  const didCompleteRef = useRef(false);

  const handleComplete = async (orgSlug: string) => {
    didCompleteRef.current = true;
    navigate({ to: '/$orgSlug/tokens', params: { orgSlug } });
  };

  useEffect(() => {
    if (!isPending && orgs && orgs.length > 0 && !didCompleteRef.current) {
      const activeOrg = orgs[0];
      navigate({ to: '/$orgSlug/tokens', params: { orgSlug: activeOrg.slug } });
    }
  }, [isPending, orgs, navigate]);

  if (isPending || (orgs && orgs.length > 0)) {
    return <CenteredSpinner />;
  }

  return (
    <OnboardingErrorBoundary>
      <OnboardingWizard open={true} onComplete={handleComplete} gitBaseUrl={gitBaseUrl} />
    </OnboardingErrorBoundary>
  );
}
